import { existsSync, readFileSync, writeFileSync, mkdirSync, accessSync, constants as fsConstants } from 'node:fs';
import { join, dirname } from 'node:path';
import { homedir } from 'node:os';
import chalk from 'chalk';
import ora from 'ora';
import { deriveConsoleUrl } from '@augmented/core';
import { success, error, info, warn } from '../lib/output.js';
import { isJsonMode, jsonOutput } from '../lib/globals.js';
import { exchangeApiKey } from '../lib/api-client.js';
import { startWatchdog } from '../lib/watchdog.js';

/**
 * Detect the user's shell profile file.
 * Returns the path to the most appropriate profile file.
 */
function detectShellProfile(): string {
  const shell = process.env['SHELL'] ?? '';
  const home = homedir();

  if (shell.includes('zsh')) {
    // Always use .zshrc — it's sourced by interactive shells.
    // .zprofile is only sourced by login shells and won't be read by subprocesses.
    return join(home, '.zshrc');
  }

  if (shell.includes('fish')) {
    const fishConfig = join(home, '.config', 'fish', 'config.fish');
    return fishConfig;
  }

  // Default to bash
  const bashrc = join(home, '.bashrc');
  if (existsSync(bashrc)) return bashrc;
  return join(home, '.bash_profile');
}

/**
 * When running as root on a POSIX host, persist AGT_HOST / AGT_API_KEY to
 * every channel that matters for system-wide visibility:
 *
 *   /etc/environment      — read by PAM (sshd logins).
 *   /etc/profile.d/agt.sh — sourced by login shells (bash -l, sh -l).
 *   /etc/bashrc           — sourced by non-login interactive bash.
 *
 * One channel isn't enough because SSM Session Manager launches `/bin/sh`
 * (bash in sh-compat mode), which skips /etc/profile.d, /etc/bashrc, and
 * isn't guaranteed to go through PAM. Users in an SSM session can `exec
 * bash` / `exec bash -l` to pick up any of these.
 *
 * Returns true if at least one channel was written.
 */
interface SystemWideEnvResult {
  etcEnvironment: boolean;
  profileD: boolean;
  bashrc: boolean;
}

function maybeWriteSystemWideEnv(
  apiUrl: string,
  apiKey: string,
  consoleUrl: string | null,
): SystemWideEnvResult {
  const empty: SystemWideEnvResult = { etcEnvironment: false, profileD: false, bashrc: false };
  if (process.platform === 'win32') return empty;
  if (typeof process.getuid !== 'function' || process.getuid() !== 0) return empty;

  return {
    etcEnvironment: writeEtcEnvironment(apiUrl, apiKey, consoleUrl),
    profileD: writeProfileDScript(apiUrl, apiKey, consoleUrl),
    // Run for its side-effect (wiring /etc/bashrc to source agt.sh) but
    // treat it as a supporting channel, not proof that env values were
    // persisted. Success reporting should reflect actual value writes.
    bashrc: ensureBashrcSourcesProfileD(),
  };
}

/** Quote a value for /etc/environment (pam_env format: KEY="value"). */
function quoteForEtcEnvironment(value: string): string {
  return `"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\r?\n/g, '\\n')}"`;
}

/** Quote a value for a POSIX shell script (single-quoted; safest — no expansion). */
function quoteForPosixShell(value: string): string {
  return `'${value.replace(/'/g, `'\\''`)}'`;
}

/**
 * Quote a value for a fish config.fish `set -gx` line. Fish evaluates `$VAR`
 * (and `$(cmd)`) inside double-quoted strings, so a raw URL containing a `$`
 * would get partially expanded. Single quotes disable expansion; embedded
 * single quotes escape as `\'`, backslashes as `\\`. Parenthesized command
 * substitutions `(cmd)` are not expanded inside double quotes in fish, but
 * single-quoting is uniformly safe.
 */
function quoteForFishShell(value: string): string {
  return `'${value.replace(/\\/g, '\\\\').replace(/'/g, `\\'`)}'`;
}

export function writeEtcEnvironment(
  apiUrl: string,
  apiKey: string,
  consoleUrl: string | null,
): boolean {
  const envPath = '/etc/environment';
  if (!existsSync(envPath)) return false;
  try {
    accessSync(envPath, fsConstants.W_OK);
  } catch {
    return false;
  }
  try {
    const current = readFileSync(envPath, 'utf-8');
    // Strip all three keys (even when CONSOLE_URL isn't being re-written) so
    // a stale value from a prior setup run can't linger while HOST/API_KEY
    // rotate to a new host underneath it.
    const stripped = current
      .split(/\r?\n/)
      .filter((line) => !/^\s*(?:export\s+)?AGT_(?:HOST|API_KEY|CONSOLE_URL)=/.test(line))
      .join('\n');
    const base = stripped.endsWith('\n') || stripped.length === 0 ? stripped : `${stripped}\n`;
    const lines = [
      `AGT_HOST=${quoteForEtcEnvironment(apiUrl)}`,
      `AGT_API_KEY=${quoteForEtcEnvironment(apiKey)}`,
    ];
    if (consoleUrl) lines.push(`AGT_CONSOLE_URL=${quoteForEtcEnvironment(consoleUrl)}`);
    const appended = `${base}${lines.join('\n')}\n`;
    writeFileSync(envPath, appended, { mode: 0o644 });
    return true;
  } catch {
    return false;
  }
}

export function writeProfileDScript(
  apiUrl: string,
  apiKey: string,
  consoleUrl: string | null,
): boolean {
  const profileD = '/etc/profile.d';
  if (!existsSync(profileD)) return false;
  try {
    accessSync(profileD, fsConstants.W_OK);
  } catch {
    return false;
  }
  try {
    const scriptPath = `${profileD}/agt.sh`;
    // Single-quote so the shell does no expansion — safe even if the value
    // contains $, backticks, or double quotes. API keys shouldn't contain
    // these, but defense in depth matters when sourcing into every login shell.
    const lines = [
      '# Augmented — auto-generated by `agt setup`. Do not edit.',
      `export AGT_HOST=${quoteForPosixShell(apiUrl)}`,
      `export AGT_API_KEY=${quoteForPosixShell(apiKey)}`,
    ];
    if (consoleUrl) lines.push(`export AGT_CONSOLE_URL=${quoteForPosixShell(consoleUrl)}`);
    lines.push('');
    writeFileSync(scriptPath, lines.join('\n'), { mode: 0o644 });
    return true;
  } catch {
    return false;
  }
}

function ensureBashrcSourcesProfileD(): boolean {
  const bashrc = '/etc/bashrc';
  if (!existsSync(bashrc)) return false;
  try {
    accessSync(bashrc, fsConstants.W_OK);
  } catch {
    return false;
  }
  try {
    const current = readFileSync(bashrc, 'utf-8');
    const marker = '# Augmented (agt) — source system-wide AGT env';
    if (current.includes(marker)) return true; // already wired up
    const snippet = [
      '',
      marker,
      'if [ -r /etc/profile.d/agt.sh ]; then',
      '  . /etc/profile.d/agt.sh',
      'fi',
      '',
    ].join('\n');
    writeFileSync(bashrc, current + snippet);
    return true;
  } catch {
    return false;
  }
}

/**
 * Build export lines for the shell profile.
 */
export function buildExportLines(
  shell: string,
  apiUrl: string,
  apiKey: string,
  consoleUrl: string | null,
): string {
  if (shell.includes('fish')) {
    const lines = [
      '',
      '# Augmented (agt) host configuration',
      `set -gx AGT_HOST ${quoteForFishShell(apiUrl)}`,
      `set -gx AGT_API_KEY ${quoteForFishShell(apiKey)}`,
    ];
    if (consoleUrl) lines.push(`set -gx AGT_CONSOLE_URL ${quoteForFishShell(consoleUrl)}`);
    lines.push('');
    return lines.join('\n');
  }

  const lines = [
    '',
    '# Augmented (agt) host configuration',
    `export AGT_HOST=${quoteForPosixShell(apiUrl)}`,
    `export AGT_API_KEY=${quoteForPosixShell(apiKey)}`,
  ];
  if (consoleUrl) lines.push(`export AGT_CONSOLE_URL=${quoteForPosixShell(consoleUrl)}`);
  lines.push('');
  return lines.join('\n');
}

export interface SetupOptions {
  /**
   * Override the API host the provisioning token is exchanged against.
   * Takes precedence over `AGT_HOST`. ENG-5831: required when `AGT_HOST`
   * is not set — the setup command no longer silently defaults to prod,
   * since a non-prod-stage token would otherwise be exchanged against the
   * wrong API and the failure looks like opaque connectivity loss.
   */
  apiHost?: string;
}

export async function setupCommand(token: string, options: SetupOptions = {}): Promise<void> {
  const json = isJsonMode();

  // Accept both new short format (XXXX-XXXX) and legacy prov_ format
  const shortTokenPattern = /^[A-HJ-NP-Z2-9]{4}-[A-HJ-NP-Z2-9]{4}$/i;
  const legacyTokenPattern = /^prov_[a-f0-9]{64}$/i;
  if (!token || (!shortTokenPattern.test(token) && !legacyTokenPattern.test(token))) {
    if (json) {
      jsonOutput({ ok: false, error: 'Invalid provisioning token. Expected format: XXXX-XXXX or prov_<64 hex>' });
    } else {
      error('Invalid provisioning token. Expected format: XXXX-XXXX or prov_<64 hex>');
      info('Get a provisioning token from the Augmented dashboard after creating a host.');
    }
    process.exitCode = 1;
    return;
  }

  // Normalize: short tokens to uppercase, legacy tokens to lowercase
  const normalizedToken = shortTokenPattern.test(token) ? token.toUpperCase() : token.toLowerCase();

  // Step 1: Determine API URL
  // ENG-5831: resolution chain is --api-host flag → AGT_HOST env → fail
  // loud. The previous silent prod default routed non-prod-stage tokens
  // against prod and the failure surfaced as opaque connectivity loss.
  // setupResult.api_url (returned by /host/setup) is the authoritative
  // value that gets persisted to the shell profile; this `apiUrl` is just
  // the bootstrap address used to reach /host/setup in the first place.
  const apiHostFlag = options.apiHost?.trim();
  const envHost = process.env['AGT_HOST']?.trim();
  const apiUrl = apiHostFlag || envHost;
  if (!apiUrl) {
    const msg =
      'Cannot determine API host for token exchange. Pass --api-host <url> or set AGT_HOST before running `agt setup`.\n' +
      '  Production:        --api-host https://api.augmented.team\n' +
      '  Non-prod stage:    --api-host https://<stage>.api.staging.augmented.team\n' +
      '  Local development: --api-host http://api.agt.localhost:1355';
    if (json) {
      jsonOutput({ ok: false, error: msg });
    } else {
      error(msg);
    }
    process.exitCode = 1;
    return;
  }
  if (!json) {
    if (apiHostFlag) {
      info(`Exchanging token against ${chalk.bold(apiUrl)} (from --api-host)`);
    } else {
      info(`Exchanging token against ${chalk.bold(apiUrl)} (from AGT_HOST)`);
    }
  }

  // Step 2: Exchange provisioning token
  const spinner = ora({ text: 'Exchanging provisioning token\u2026', isSilent: json });
  spinner.start();

  let setupResult: {
    host_name: string;
    host_id: string;
    team_slug: string | null;
    api_url: string;
    api_key: string;
    agents: string[];
  };

  try {
    const res = await fetch(`${apiUrl}/host/setup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: normalizedToken }),
    });

    if (!res.ok) {
      const body = await res.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error((body['error'] as string) ?? `Setup failed: HTTP ${res.status}`);
    }

    setupResult = await res.json() as typeof setupResult;
    spinner.succeed('Provisioning token accepted');
  } catch (err) {
    spinner.fail('Failed to exchange provisioning token');
    if (json) {
      jsonOutput({ ok: false, error: (err as Error).message });
    } else {
      error((err as Error).message);
    }
    process.exitCode = 1;
    return;
  }

  // Use the API URL returned by the server (more authoritative)
  const finalApiUrl = setupResult.api_url || apiUrl;

  // Set for current process so verification can run
  process.env['AGT_HOST'] = finalApiUrl;
  process.env['AGT_API_KEY'] = setupResult.api_key;

  // Step 3: Verify connection before writing anything to disk
  const verifySpinner = ora({ text: 'Verifying connection\u2026', isSilent: json });
  verifySpinner.start();

  try {
    const exchange = await exchangeApiKey(setupResult.api_key);
    verifySpinner.succeed('Connection verified');

    if (!json) {
      info(`Host:       ${chalk.bold(setupResult.host_name)}`);
      info(`Host ID:    ${exchange.hostId}`);
      info(`Team:       ${chalk.bold(exchange.teamSlug ?? setupResult.team_slug ?? 'unknown')}`);
      if (exchange.userEmail) {
        info(`User:       ${chalk.bold(exchange.userEmail)}`);
      }
    }
  } catch (err) {
    verifySpinner.fail('Connection verification failed');
    if (json) {
      jsonOutput({ ok: false, error: (err as Error).message });
    } else {
      error((err as Error).message);
      info('Connection could not be verified. No changes were written to your shell profile.');
      info('Check your network connection and try: agt whoami');
    }
    process.exitCode = 1;
    return;
  }

  // Step 4: Write env vars to shell profile (only after successful verification)
  const profilePath = detectShellProfile();
  const shell = process.env['SHELL'] ?? 'bash';

  // ENG-4495: derive the webapp console URL from the API URL so the
  // schedule-edit deep-link footer (ENG-4462) works on fresh hosts without
  // the operator having to export AGT_CONSOLE_URL manually. Returns null
  // for non-standard api.<host> shapes; we persist HOST/API_KEY anyway and
  // flag the gap so the operator can set CONSOLE_URL themselves.
  const consoleUrl = deriveConsoleUrl(finalApiUrl);
  // Propagate to the current process so the watchdog started later in this
  // same command inherits it — otherwise the first-run schedule deep-link
  // footer would stay disabled until the next restart even though we wrote
  // the value to the shell rc file.
  if (consoleUrl) {
    process.env['AGT_CONSOLE_URL'] = consoleUrl;
  }
  if (!json && !consoleUrl) {
    warn(
      `Could not derive AGT_CONSOLE_URL from ${finalApiUrl} — scheduled-delivery deep links will be disabled until you set it manually.`,
    );
  }

  const exportLines = buildExportLines(shell, finalApiUrl, setupResult.api_key, consoleUrl);
  const profileDir = dirname(profilePath);
  if (!existsSync(profileDir)) {
    mkdirSync(profileDir, { recursive: true });
  }

  // Replace existing block if present, otherwise append
  const marker = '# Augmented (agt) host configuration';
  const current = existsSync(profilePath) ? readFileSync(profilePath, 'utf-8') : '';
  let updated: string;
  if (current.includes(marker)) {
    // Match the marker line and all subsequent export/set lines (bash + fish),
    // plus any surrounding blank lines that were part of the original block.
    updated = current.replace(
      /\n?# Augmented \(agt\) host configuration\n(?:(?:export\s+AGT_\w+=.*|set\s+-gx\s+AGT_\w+\s+.*)\n)*\n?/,
      exportLines,
    );
    if (!json) {
      info('Replacing existing Augmented config block in shell profile.');
    }
  } else {
    updated = current + exportLines;
  }
  writeFileSync(profilePath, updated.endsWith('\n') ? updated : `${updated}\n`);

  if (!json) {
    success(`Environment variables written to ${chalk.bold(profilePath)}`);
  }

  // Step 4b: When running as root, fan out AGT_HOST / AGT_API_KEY to the
  // system-wide channels (/etc/environment, /etc/profile.d/agt.sh, and a
  // one-time /etc/bashrc source line). No single channel covers every
  // session type — SSM Session Manager launches `sh` which skips most of
  // them, so we write to all and let operators `exec bash -l` if needed.
  const sys = maybeWriteSystemWideEnv(finalApiUrl, setupResult.api_key, consoleUrl);
  // Bashrc only wires up the source — if neither of the value channels
  // actually persisted the vars, we shouldn't claim success.
  const valueChannelsWritten = sys.etcEnvironment || sys.profileD;
  if (!json && valueChannelsWritten) {
    const channels = [
      sys.etcEnvironment ? '/etc/environment' : null,
      sys.profileD ? '/etc/profile.d/agt.sh' : null,
      sys.bashrc ? '/etc/bashrc' : null,
    ].filter(Boolean).join(' + ');
    success(`System-wide env updated: ${channels}`);
  }

  // Step 5: Start manager daemon.
  // Detach so the setup process can exit cleanly — critical for JSON-mode
  // callers like the EC2 bootstrap that capture stdout via `$(...)`. Without
  // detach, in-process timers keep Node alive and the capture hangs forever.
  const managerSpinner = ora({ text: 'Starting manager daemon\u2026', isSilent: json });
  managerSpinner.start();

  try {
    const configDir = join(homedir(), '.augmented');
    const { pid } = startWatchdog({ intervalMs: 10_000, configDir, detached: true });
    managerSpinner.succeed(`Manager started (PID ${pid})`);
  } catch (err) {
    managerSpinner.warn('Could not start manager daemon');
    if (!json) {
      warn((err as Error).message);
      info('Start it manually with: agt manager start');
    }
  }

  // Step 6: Print summary
  //
  // The raw API key is emitted to stdout only when the caller explicitly
  // opts in via AGT_SETUP_INCLUDE_API_KEY=1. Default-off because --json
  // output can end up in automation logs; trusted bootstrap flows (EC2
  // user-data) set the env var before invoking so they can persist the
  // key system-wide.
  const includeApiKey = process.env['AGT_SETUP_INCLUDE_API_KEY'] === '1';
  if (json) {
    jsonOutput({
      ok: true,
      host_name: setupResult.host_name,
      host_id: setupResult.host_id,
      team_slug: setupResult.team_slug,
      api_url: finalApiUrl,
      ...(includeApiKey ? { api_key: setupResult.api_key } : {}),
      agents: setupResult.agents,
      profile: profilePath,
    });
    // Force-exit so bootstrap scripts using `$(agt setup --json TOKEN)`
    // don't hang on stray open handles (fetch keepalive, Supabase realtime
    // warmup, etc.). The manager daemon runs in a detached child and keeps
    // going regardless.
    process.exit(0);
  } else {
    console.log();
    console.log(chalk.green.bold('  Setup complete!'));
    console.log();
    if (setupResult.agents.length > 0) {
      info(`Agents:     ${setupResult.agents.map((a) => chalk.cyan(a)).join(', ')}`);
    } else {
      info('No agents assigned yet. Assign agents in the dashboard or with: agt host assign');
    }
    console.log();
    info(`Restart your shell or run: ${chalk.bold(`source ${profilePath}`)}`);
  }
}
